import { useMutation } from '@apollo/client/react';
import { Box, TextField } from '@mui/material';
import React from 'react';

import {
  AdminCreateClientDocument,
  AdminUpdateClientDocument,
} from '../../../graphql/types';
import { FieldName } from './FieldNames';
import type { StepProps } from './types';

const REQUIRED = 'Client name and display name are required.';

/**
 * Step 1 of a new client. Before the client exists it creates it; afterwards
 * it edits it. MA-7615 adds the logo and the final wording.
 */
export const BasicsStep: React.FC<StepProps> = ({
  launch,
  readOnly,
  onEdit,
  registerSave,
  registerCreateClient,
}) => {
  const client = launch?.client;
  const [name, setName] = React.useState(client?.name ?? '');
  const [displayName, setDisplayName] = React.useState(
    client?.displayName ?? '',
  );
  const [showErrors, setShowErrors] = React.useState(false);
  const [createClient] = useMutation(AdminCreateClientDocument);
  const [updateClient] = useMutation(AdminUpdateClientDocument);

  const checkRequired = React.useCallback(() => {
    if (!name.trim() || !displayName.trim()) {
      setShowErrors(true);
      throw new Error(REQUIRED);
    }
    return { name: name.trim(), displayName: displayName.trim() };
  }, [name, displayName]);

  React.useEffect(() => {
    if (client) {
      if (!registerSave) {
        return;
      }
      registerSave(async () => {
        await updateClient({
          variables: { input: { id: client.id, ...checkRequired() } },
        });
      });
      return () => registerSave(undefined);
    }
    if (!registerCreateClient) {
      return;
    }
    registerCreateClient(async () => {
      const { data } = await createClient({
        variables: { input: checkRequired() },
      });
      const id = data?.data.client.id;
      if (!id) {
        throw new Error('The client was not created.');
      }
      return id;
    });
    return () => registerCreateClient(undefined);
  }, [
    client,
    checkRequired,
    createClient,
    updateClient,
    registerSave,
    registerCreateClient,
  ]);

  const field = (
    id: string,
    label: string,
    helperText: string,
    value: string,
    setValue: (value: string) => void,
  ) => (
    <Box>
      <TextField
        id={id}
        label={label}
        required
        fullWidth
        size="small"
        value={value}
        disabled={readOnly}
        error={showErrors && !value.trim()}
        helperText={
          showErrors && !value.trim() ? `${label} is required.` : helperText
        }
        onChange={(event) => {
          setValue(event.target.value);
          onEdit();
        }}
      />
      <FieldName>clients.{id}</FieldName>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      }}
    >
      {field(
        'name',
        'Client name',
        'Internal name used across admin.',
        name,
        setName,
      )}
      {field(
        'displayName',
        'Display name',
        'Shown to members in the app.',
        displayName,
        setDisplayName,
      )}
    </Box>
  );
};
